import { defineStore } from 'pinia';
import { ref } from 'vue';
import { createAnimation, type Animation } from '@ionic/vue';
import { logger } from '@/common/services/config';

export interface AnimationConfig {
  duration?: number;
  easing?: string;
  fromOpacity?: string;
  toOpacity?: string;
  fromTransform?: string;
  toTransform?: string;
}

export const useAnimationStore = defineStore('animation', () => {
  // State
  const currentAnimation = ref<Animation | null>(null);
  const isAnimating = ref(false);

  // Animation presets
  const presets = {
    slideInRight: {
      duration: 500,
      easing: 'ease-in-out',
      fromOpacity: '0',
      toOpacity: '1',
      fromTransform: 'translateX(100px)',
      toTransform: 'translateX(0px)',
    },
    slideInLeft: {
      duration: 500,
      easing: 'ease-in-out',
      fromOpacity: '0',
      toOpacity: '1',
      fromTransform: 'translateX(-100px)',
      toTransform: 'translateX(0px)',
    },
    slideInUp: {
      duration: 500,
      easing: 'ease-in-out',
      fromOpacity: '0',
      toOpacity: '1',
      fromTransform: 'translateY(100px)',
      toTransform: 'translateY(0px)',
    },
    slideInDown: {
      duration: 500,
      easing: 'ease-in-out',
      fromOpacity: '0',
      toOpacity: '1',
      fromTransform: 'translateY(-100px)',
      toTransform: 'translateY(0px)',
    },
    fadeIn: {
      duration: 400,
      easing: 'ease-in-out',
      fromOpacity: '0',
      toOpacity: '1',
      fromTransform: 'scale(0.95)',
      toTransform: 'scale(1)',
    },
    zoomIn: {
      duration: 400,
      easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      fromOpacity: '0',
      toOpacity: '1',
      fromTransform: 'scale(0.8)',
      toTransform: 'scale(1)',
    },
    bounceIn: {
      duration: 600,
      easing: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
      fromOpacity: '0',
      toOpacity: '1',
      fromTransform: 'scale(0.3)',
      toTransform: 'scale(1)',
    },
    flipIn: {
      duration: 700,
      easing: 'ease-in-out',
      fromOpacity: '0',
      toOpacity: '1',
      fromTransform: 'rotateY(-90deg)',
      toTransform: 'rotateY(0deg)',
    },
  };

  // Actions
  function createContentAnimation(
    element: HTMLElement,
    config: AnimationConfig = presets.slideInRight
  ): Animation {
    const animation = createAnimation()
      .addElement(element)
      .duration(config.duration || 500)
      .easing(config.easing || 'ease-in-out')
      .fromTo('opacity', config.fromOpacity || '0', config.toOpacity || '1')
      .fromTo(
        'transform',
        config.fromTransform || 'translateX(100px)',
        config.toTransform || 'translateX(0px)'
      );

    return animation;
  }

  async function playContentAnimation(
    element: HTMLElement,
    animationType: keyof typeof presets = 'slideInRight'
  ): Promise<void> {
    // Stop current animation if running
    if (currentAnimation.value) {
      currentAnimation.value.stop();
    }

    isAnimating.value = true;

    try {
      const config = presets[animationType];
      currentAnimation.value = createContentAnimation(element, config);

      await currentAnimation.value.play();
    } catch (error) {
      logger.error('Animation error:', error);
    } finally {
      isAnimating.value = false;
    }
  }

  function stopCurrentAnimation(): void {
    if (currentAnimation.value) {
      currentAnimation.value.stop();
      currentAnimation.value = null;
    }
    isAnimating.value = false;
  }

  // Route-based animation mapping
  function getAnimationForRoute(routeName: string): keyof typeof presets {
    // Map different routes to different animations
    const routeAnimations: Record<string, keyof typeof presets> = {
      StudentHome: 'slideInRight',
      EmployeeHome: 'slideInRight',
      Notes: 'slideInUp',
      Planning: 'slideInLeft',
      Disponibilities: 'slideInLeft',
      Settings: 'slideInDown',
      Profile: 'fadeIn',
      Search: 'zoomIn',
      Login: 'bounceIn',
      HomePage: 'flipIn',
    };

    return routeAnimations[routeName] || 'slideInRight';
  }

  return {
    // State
    currentAnimation,
    isAnimating,
    presets,

    // Actions
    createContentAnimation,
    playContentAnimation,
    stopCurrentAnimation,
    getAnimationForRoute,
  };
});
